import type { Layout } from "@/features/simulation/types";
import type { TilePos } from "@/lib/pixi/isoCoords";

/**
 * ラインとマップの静的ジオメトリ定数、およびレイアウト依存の派生関数。
 *
 * 固定点:
 *  - LOADING_TILE : コンベア始点（ワーク投入口）
 *  - UNLOAD_TILE  : 搬送ロボットの積み下ろしエリア
 *  - TRANSPORT_CORNER: 搬送経路の L 字の角（固定）
 *
 * レイアウト依存（Layout から計算）:
 *  - 右端タイル / アーム位置 / 搬送ロボット待機位置 / 搬送ウェイポイント / プレス pathDist
 */

/** ワーク投入口（常に col=0, row=10） */
export const LOADING_TILE: TilePos = { col: 0, row: 10 };

/**
 * 搬送ロボの「積み下ろし位置」— ロボはここで停止して荷物を降ろす。
 * 荷物自体はさらに 1 タイル先の STORAGE_TILE に「置かれて」フェードアウトする。
 */
export const UNLOADING_TILE: TilePos = { col: 2, row: 17 };

/**
 * 積み下ろした荷物の保管場所 — フェードアウトはここで起きる。
 * ロボはここまでは行かない。
 */
export const STORAGE_TILE: TilePos = { col: 2, row: 18 };

/** 後方互換用エイリアス（旧コードが UNLOAD_TILE を参照していた箇所向け）。 */
export const UNLOAD_TILE: TilePos = STORAGE_TILE;

/**
 * 搬送経路の角（固定）。プレス機が row=9 / row=11 の両脚を持つので、
 * 搬送経路は row=12 を通すことで脚と干渉しないようにする。
 */
export const TRANSPORT_CORNER: TilePos = { col: 2, row: 12 };

/** デフォルトレイアウト */
export const DEFAULT_LAYOUT: Layout = {
  conveyorLength: 12,
  pressPosition: 6,
  bufferCapacity: 3,
  transportCapacity: 3,
};

/** 有効なレイアウトの範囲 */
export const LAYOUT_BOUNDS = {
  conveyorLength: { min: 5, max: 19 },
  pressPositionFromRightEnd: { minGap: 1 }, // プレスと右端の間に最低1タイル
  bufferCapacity: { min: 1, max: 10 },
  transportCapacity: { min: 1, max: 10 },
} as const;

/** pressPosition の有効範囲（conveyorLength に依存） */
export function pressPositionRange(conveyorLength: number): { min: number; max: number } {
  return { min: 1, max: Math.max(1, conveyorLength - 2) };
}

/** pressPosition を conveyorLength の範囲にクランプ */
export function clampPressPosition(pressPosition: number, conveyorLength: number): number {
  const { min, max } = pressPositionRange(conveyorLength);
  const int = Math.round(pressPosition);
  return Math.min(max, Math.max(min, int));
}

/**
 * ワーク同士の最小間隔（タイル単位） — 詰まり判定用。
 * 1.0 にすることで停止時に「1 タイルに 1 ワーク」が正確に成立する。
 */
export const MIN_WORK_SPACING = 1.0;

/**
 * コンベア終端の仮置き場（バッファ）に置けるワークのデフォルト最大数。
 * 実行時の値は `state.layout.bufferCapacity` を参照する（UI から変更可能）。
 */
export const BUFFER_CAPACITY = 3;

/**
 * 搬送ロボが一度に積載できるワークのデフォルト最大数。
 * 実行時の値は `state.layout.transportCapacity` を参照する（UI から変更可能）。
 * cargo がここに達したら出発する。
 */
export const TRANSPORT_CAPACITY = 3;

/**
 * 1 個のワークが連続して何秒停止したらコンベアを自動停止するかの閾値。
 * この秒数以上「前進できずに停まっている」ワークがあれば、滞留とみなす。
 */
export const CONVEYOR_JAM_THRESHOLD_SEC = 2;

/** 積み下ろしフェードアウトの所要秒数 */
export const UNLOAD_DURATION_SEC = 0.8;

/** 1tickでの dt 上限（タブ復帰時の暴走防止） */
export const MAX_TICK_DT = 0.1;

/** パラメータのデフォルト値（全て整数刻み前提） */
export const DEFAULT_PARAMS = {
  conveyorSpeed: 1,
  pressProcessingTime: 2,
  armTime: 2,
  transportSpeed: 1,
} as const;

export const DEFAULT_SPAWN_INTERVAL_SEC = 2;

/** パラメータの UI レンジ（整数刻み） */
export const PARAM_BOUNDS = {
  conveyorSpeed: { min: 1, max: 10, step: 1 },
  pressProcessingTime: { min: 1, max: 20, step: 1 },
  armTime: { min: 1, max: 20, step: 1 },
  transportSpeed: { min: 1, max: 10, step: 1 },
  autoSpawnInterval: { min: 1, max: 20, step: 1 },
  queuedMaxN: { min: 1, max: 50, step: 1 },
} as const;

// --- Layout から派生する位置情報 ---

/** コンベア経路長（距離、タイル単位） */
export function getPathLength(layout: Layout): number {
  return layout.conveyorLength - 1;
}

/** プレス機の pathDist 上の位置（= pressPosition の col） */
export function getPressPathDist(layout: Layout): number {
  return layout.pressPosition;
}

/** プレス機のタイル座標 */
export function getPressTile(layout: Layout): TilePos {
  return { col: layout.pressPosition, row: 10 };
}

/** コンベア右端のタイル座標 */
export function getRightEndTile(layout: Layout): TilePos {
  return { col: layout.conveyorLength - 1, row: 10 };
}

/**
 * ロボットアームのタイル座標。ラインの右端のさらに右隣（1 タイル外）に配置。
 * これにより、アームから見て:
 *   - 西 (col-1) の隣タイル = コンベア右端（ピックアップ位置）
 *   - 南 (row+1) の隣タイル = 搬送ロボ待機位置（積み込み位置 / 配置位置）
 * の 2 方向（90°回転）でワークを移送できる。
 */
export function getArmTile(layout: Layout): TilePos {
  return { col: layout.conveyorLength, row: 10 };
}

/** 搬送ロボットの待機位置（アームの南隣 = 積み込み位置） */
export function getTransportParking(layout: Layout): TilePos {
  return { col: layout.conveyorLength, row: 11 };
}

/**
 * 搬送ロボットのウェイポイント列（行き）。
 * パス: 待機 → (col, 12) → コーナー(2, 12) → 積み下ろし(2, 17)。
 * row=11 を通らないのは、プレスの南脚 (row=11) と干渉させないため。
 */
export function getTransportWaypoints(layout: Layout): TilePos[] {
  const parking = getTransportParking(layout);
  return [
    parking,
    { col: parking.col, row: TRANSPORT_CORNER.row }, // 1 タイル南へ降りて row=12 へ
    TRANSPORT_CORNER,
    UNLOADING_TILE,
  ];
}

/**
 * 搬送ロボが通過する経路タイル（描画用: 着色対象）。
 * parking → 1tile south → corner → UNLOADING_TILE の S 字経路の全タイルを返す。
 * STORAGE_TILE は経路には含めない（ロボは行かない）。
 */
export function getTransportPathTiles(layout: Layout): TilePos[] {
  const parking = getTransportParking(layout);
  const tiles: TilePos[] = [];
  // セグメント1: parking col, row=parking.row..TRANSPORT_CORNER.row
  for (let r = parking.row; r <= TRANSPORT_CORNER.row; r++) {
    tiles.push({ col: parking.col, row: r });
  }
  // セグメント2: row=TRANSPORT_CORNER.row, col=parking.col-1..TRANSPORT_CORNER.col
  for (let c = parking.col - 1; c >= TRANSPORT_CORNER.col; c--) {
    tiles.push({ col: c, row: TRANSPORT_CORNER.row });
  }
  // セグメント3: col=TRANSPORT_CORNER.col, row=TRANSPORT_CORNER.row+1..UNLOADING_TILE.row
  for (let r = TRANSPORT_CORNER.row + 1; r <= UNLOADING_TILE.row; r++) {
    tiles.push({ col: TRANSPORT_CORNER.col, row: r });
  }
  return tiles;
}

/** プレス機の北側脚タイル（コンベア row=10 の 1 つ北 = row=9） */
export function getPressNorthLegTile(layout: Layout): TilePos {
  return { col: layout.pressPosition, row: 9 };
}

/** プレス機の南側脚タイル（コンベア row=10 の 1 つ南 = row=11） */
export function getPressSouthLegTile(layout: Layout): TilePos {
  return { col: layout.pressPosition, row: 11 };
}

/**
 * バッファのタイル位置（コンベア終端の 1 タイル）。
 * すべてのスロット（0..bufferCapacity-1）がこの 1 タイル上に垂直スタックされる。
 * 搬送ロボの cargo と同様の見せ方。
 */
export function getBufferTile(layout: Layout): TilePos {
  return { col: layout.conveyorLength - 1, row: 10 };
}

/** 占有タイル描画用に同じタイルを返す（旧 API 互換） */
export function getBufferTiles(layout: Layout): TilePos[] {
  return [getBufferTile(layout)];
}
